import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import ProgressStepper from "./components/ProgressStepper";
import LogoSvg from "./assets/Logo.svg";
import Step0 from "./components/steps/Step0.jsx";
import Step1 from "./components/steps/Step1.jsx";
import Step2 from "./components/steps/Step2.jsx";
import Step3 from "./components/steps/Step3.jsx";
import Step4 from "./components/steps/Step4.jsx";
import Step5 from "./components/steps/Step5.jsx";
import { useLocation, useNavigate } from 'react-router-dom';
import {
  QUIZ_STEPS,
  STORAGE_KEYS,
  QUIZ_OPTIONS_DATA,
} from "./constants";
import { useQuizSelections } from "./hooks/useLocalStorage";
import { preloadNextStepImages } from "./utils/imagePreloader";
import { initTracking } from "./utils/tracking";
import { useSteps } from "./hooks/useSteps";

const STEPS_COMPONENTS = {
  welcome: Step0,
  step1: Step1,
  step2: Step2,
  step3: Step3,
  step4: Step4,
  step5: Step5,
};

const validateStepData = (step, answer) => {
  switch (step) {
    case "welcome":
      return answer && answer.trim() !== "";
    case "step1":
      return answer && answer.ethnicity && answer.age;
    case "step2":
      return answer && answer.bodyType && answer.breastSize && answer.buttSize;
    case "step3":
      return answer && answer.hairColor && answer.hairStyle && answer.eyeColor;
    case "step4":
      return answer && answer.voice && answer.voiceAudio && answer.personality;
    case "step5":
      return answer && answer.trim() !== "";
    default:
      return true;
  }
};

function Home() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [displayedStep, setDisplayedStep] = useState(0);
  const [transitionState, setTransitionState] = useState('idle'); // idle | fade-out | fade-in

  const location = useLocation();
  const navigate = useNavigate();
  const [selections, setSelections] = useQuizSelections(location.pathname);
  const pendingStepRef = useRef(null);

  const { getActiveSteps } = useSteps();

  const activeSteps = useMemo(() => {
    return getActiveSteps(QUIZ_STEPS);
  }, [getActiveSteps]);

  const hiddenSteps = useMemo(() => {
    const loadingIndex = activeSteps.indexOf('loading');
    const createAccountIndex = activeSteps.indexOf('createAccount');
    const loginIndex = activeSteps.indexOf('login');
    const step5Index = activeSteps.indexOf('step5');

    return [loadingIndex, createAccountIndex, loginIndex, step5Index].filter(
      (index) => index !== -1,
    );
  }, [activeSteps]);

  useEffect(() => {
    document.body.classList.add('ndg-mode');
    document.body.classList.add('quiz-mode');
    return () => {
      document.body.classList.remove('ndg-mode');
      document.body.classList.remove('quiz-mode');
    };
  }, []);

  useEffect(() => {
    if (activeSteps[currentStep] === 'step5') {
      navigate('summary', { replace: true });
    }
  }, [currentStep, activeSteps, navigate]);

  const transitionToStep = useCallback(
    (newStep) => {
      if (transitionState !== 'idle') return;

      pendingStepRef.current = newStep;
      setTransitionState('fade-out');
    },
    [transitionState],
  );

  const handleAnswer = useCallback(
    (step, answer) => {
      if (!validateStepData(step, answer)) return;

      if (step === 'welcome' || step.startsWith('step')) {
        const existing = JSON.parse(
          localStorage.getItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS) || '{}',
        );

        const updated = {
          ...existing,
          [step]: answer,
        };

        localStorage.setItem(
          STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS,
          JSON.stringify(updated),
        );
      }

      setSelections((prev) => ({
        ...prev,
        [step]: answer,
      }));

      setTimeout(() => {
        preloadNextStepImages(step, QUIZ_OPTIONS_DATA);
      }, 100);

      transitionToStep(currentStep + 1);
    },
    [setSelections, currentStep, transitionToStep],
  );

  const handlePrevious = useCallback(() => {
    if (transitionState !== 'idle' || currentStep === 0) return;

    transitionToStep(currentStep - 1);
  }, [transitionState, currentStep, transitionToStep]);

  const resetQuiz = useCallback(() => {
    setCurrentStep(0);
    setDisplayedStep(0);
    setSelections({});
    setTransitionState('idle');

    localStorage.removeItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS);
  }, [setSelections]);

  useEffect(() => {
    if (transitionState === 'fade-out') {
      const timer = setTimeout(() => {
        setCurrentStep(pendingStepRef.current);
        setDisplayedStep(pendingStepRef.current);
        setTransitionState('fade-in');
      }, 300);

      return () => clearTimeout(timer);
    }

    if (transitionState === 'fade-in') {
      const timer = setTimeout(() => {
        setTransitionState('idle');
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [transitionState]);

  useEffect(() => {
    initTracking();
  }, []);

  const getVisibleSteps = useCallback(
    () => activeSteps.filter((_, i) => !hiddenSteps.includes(i)),
    [activeSteps, hiddenSteps],
  );

  const shouldShowProgressStepper = useCallback(
    () => !hiddenSteps.includes(currentStep),
    [currentStep, hiddenSteps],
  );

  const currentStepName = activeSteps[displayedStep];
  const CurrentStepComponent = STEPS_COMPONENTS[currentStepName];
  const isTransitioning = transitionState !== 'idle';

  // step5 is owned by the /summary route (see SummaryPage in App.jsx). The effect
  // above navigates there as soon as currentStep reaches step5, so rendering Step5
  // inline here too would mount it twice and fire `step_6` twice. Render nothing.
  if (currentStepName === 'step5') {
    return null;
  }

  if (!CurrentStepComponent) {
    return (
      <div className="quizContentWrapper">
        <div className="quizContent">
          <h1>{t('step_not_found')}</h1>
          <button onClick={resetQuiz}>{t('reset_quiz')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`step-wrapper ${
      transitionState === 'fade-out'
        ? 'fade-out'
        : transitionState === 'fade-in'
        ? 'fade-in'
        : ''
    }`}>
      <header className='quiz-header'>
        <img src={LogoSvg} alt='Swipey' className='quiz-header-logo' width={100} height={24} />
      </header>
      {shouldShowProgressStepper() && (
        <ProgressStepper
          currentStep={currentStep}
          totalSteps={getVisibleSteps().length}
        />
      )}
      <CurrentStepComponent
        onAnswer={handleAnswer}
        selections={selections}
        onPrevious={handlePrevious}
        isTransitioning={isTransitioning}
      />
    </div>
  );
}

export default Home;
